import { Box, Stack, Typography } from "@mui/material"
import { Repeat } from "lib/@components/repeat"
import { If } from "lib/@components/switch"
import { PdfHtmlDiff } from "./pdf-html-diff"
import { PdfTaskClassificationMarker } from "./pdf-task-classification-marker"
import {
    formatFrequencyLabel,
    formatTaskDurationLabel,
    normaliseHtml,
    normaliseText,
    sanitizePdfHtml,
} from "./pdf-formatters"
import { shouldRenderPlaceholder } from "./pdf-task-comparison-helpers"
import { PdfRichTextBlock } from "./pdf-rich-text-block"
import { PdfTaskMetadataItem } from "./pdf-task-metadata-item"
import { buildTaskMetadataRows } from "./pdf-task-metadata-rows.mjs"
import { PdfTaskPlaceholder } from "./pdf-task-placeholder"

export function PdfTaskPanel({ task, otherTask, status, side, showUnchangedNote }) {
    if (shouldRenderPlaceholder({ task, status, side, showUnchangedNote })) {
        return <PdfTaskPlaceholder status={status} side={side} />
    }

    if (!task) {
        return <PdfTaskPlaceholder status="missing" side={side} />
    }

    const metadata = buildMetadata(task)
    const shouldDiff = status === "modified"
    const showRemoved = side === "left"
    const safeTaskContent = sanitizePdfHtml(task.content)
    const safeOtherTaskContent = sanitizePdfHtml(otherTask?.content)
    const safeTaskNote = sanitizePdfHtml(task.note)
    const safeOtherTaskNote = sanitizePdfHtml(otherTask?.note)
    const normalisedPdfTitle = normaliseText(task.title)

    return (
        <Box sx={{ minHeight: "100%" }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: "10px" }}>
                <PdfTaskClassificationMarker classification={task.classification} />
                <Typography sx={{ fontWeight: 700, fontSize: "11px", color: "#1f2937" }}>
                    {task.displayIndex ? `${task.displayIndex}. ` : ""}
                    {normalisedPdfTitle}
                </Typography>
            </Stack>
            <Stack direction="row" spacing={2} sx={{ mb: "12px", flexWrap: "wrap", rowGap: "6px" }}>
                <Repeat list={metadata} item={<PdfTaskMetadataItem />} />
            </Stack>
            <If value={shouldDiff}>
                <PdfHtmlDiff
                    text={safeTaskContent}
                    original={safeOtherTaskContent}
                    other={safeOtherTaskContent}
                    showRemoved={showRemoved}
                />
            </If>
            <If value={!shouldDiff}>
                <PdfRichTextBlock html={safeTaskContent} />
            </If>
            <If value={hasTaskNotes(task, otherTask)}>
                <Box sx={{ mt: "14px" }}>
                    <Typography sx={{ fontWeight: 700, fontSize: "10px", color: "#1f2937", mb: "8px" }}>
                        NOTES
                    </Typography>
                    <If value={shouldDiff}>
                        <PdfHtmlDiff
                            text={safeTaskNote}
                            original={safeOtherTaskNote}
                            other={safeOtherTaskNote}
                            showRemoved={showRemoved}
                        />
                    </If>
                    <If value={!shouldDiff}>
                        <PdfRichTextBlock html={safeTaskNote} />
                    </If>
                </Box>
            </If>
        </Box>
    )
}

function buildMetadata(task = {}) {
    const frequency = formatFrequencyLabel(task.frequency)
    return buildTaskMetadataRows(task, {
        frequencyLabel: frequency?.shortLabel,
        durationLabel: formatTaskDurationLabel(task.minutes),
    })
}

function hasTaskNotes(task, otherTask) {
    return Boolean(normaliseHtml(task?.note) || normaliseHtml(otherTask?.note))
}
